import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Paper,
  CircularProgress,
  Box,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  Container,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMenuPreview } from 'apis/menuAPIs';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[50],
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  banner: {
    marginBottom: theme.spacing(3),
  },
  category: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  categoryName: {
    fontWeight: 600,
  },
  item: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2),
  },
  itemName: {
    fontWeight: 500,
  },
  price: {
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  group: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    borderLeft: `2px solid ${theme.palette.divider}`,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  option: {
    color: theme.palette.text.secondary,
  },
  centred: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
}));

const formatPrice = (price, currency) => {
  const value = Number(price);
  if (!Number.isFinite(value)) {
    return '';
  }
  return `${currency === 'AUD' ? '$' : ''}${value.toFixed(2)}`;
};

// force_min/force_max are the whole reason to preview a banquet, so state the
// rule in words instead of making someone infer it from two numbers.
const describeConstraint = (group) => {
  const min = Number(group.force_min) || 0;
  const max = Number(group.force_max) || 0;

  if (min === 1 && max === 1) {
    return 'Choose 1';
  }
  if (min > 0 && max > min) {
    return `Choose ${min} to ${max}`;
  }
  if (min > 0) {
    return `Choose at least ${min}`;
  }
  if (max > 0) {
    return `Choose up to ${max}`;
  }
  return 'Optional';
};

const OptionGroup = ({ group, currency, classes }) => {
  const options = Array.isArray(group.options) ? group.options : [];

  return (
    <Box className={classes.group}>
      <Box className={classes.groupHeader}>
        <Typography variant="body2" color="textSecondary">
          {group.name}
        </Typography>
        <Chip size="small" label={describeConstraint(group)} />
        {group.required === true && (
          <Chip size="small" color="primary" variant="outlined" label="Required" />
        )}
      </Box>

      {options.length === 0 ? (
        <Typography variant="caption" color="error">
          No options — this group disappears when the menu is published.
        </Typography>
      ) : (
        options.map((option) => (
          <Typography key={option.id} variant="body2" className={classes.option}>
            {option.name}
            {Number(option.price) > 0 && ` (+${formatPrice(option.price, currency)})`}
          </Typography>
        ))
      )}
    </Box>
  );
};

OptionGroup.propTypes = {
  group: PropTypes.shape({
    name: PropTypes.string,
    required: PropTypes.bool,
    force_min: PropTypes.number,
    force_max: PropTypes.number,
    options: PropTypes.array,
  }).isRequired,
  currency: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

const PreviewItem = ({ item, currency, classes }) => {
  const groups = Array.isArray(item.groups) ? item.groups : [];

  return (
    <Box className={classes.item}>
      <Box className={classes.itemHeader}>
        <Typography className={classes.itemName}>
          {item.name}
          {item.altName ? ` ${item.altName}` : ''}
        </Typography>
        <Typography className={classes.price}>
          {formatPrice(item.price, currency)}
        </Typography>
      </Box>

      {item.description && (
        <Typography variant="body2" color="textSecondary">
          {item.description}
        </Typography>
      )}

      {groups.map((group) => (
        <OptionGroup
          key={group.id}
          group={group}
          currency={currency}
          classes={classes}
        />
      ))}
    </Box>
  );
};

PreviewItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    altName: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    groups: PropTypes.array,
  }).isRequired,
  currency: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

const MenuPreview = () => {
  const classes = useStyles();
  const { code } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['menuPreview', code],
    queryFn: () => getMenuPreview(code),
    retry: false,
    enabled: Boolean(code),
  });

  if (isLoading) {
    return (
      <Box className={classes.centred}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const status = error.response && error.response.status;
    return (
      <Container maxWidth="sm" className={classes.root}>
        <Alert severity={status === 410 ? 'warning' : 'error'}>
          <AlertTitle>
            {status === 410 ? 'This preview has expired' : 'Preview not found'}
          </AlertTitle>
          {status === 410
            ? 'Preview links last 7 days. Generate a new one from the menu.'
            : 'Check the link, or generate a new preview from the menu.'}
        </Alert>
      </Container>
    );
  }

  const menu = (data && data.menu) || {};
  const categories = Array.isArray(menu.categories) ? menu.categories : [];
  const warnings = (data && data.warnings) || [];
  const currency = menu.currency || 'AUD';

  return (
    <Container maxWidth="sm" className={classes.root}>
      <Box className={classes.header}>
        {menu.restaurantName && (
          <Typography variant="h5">{menu.restaurantName}</Typography>
        )}
        <Typography variant="h6" color="textSecondary">
          {menu.menuName}
        </Typography>
        {menu.menuDescription && (
          <Typography variant="body2" color="textSecondary">
            {menu.menuDescription}
          </Typography>
        )}
      </Box>

      <Alert severity="info" className={classes.banner}>
        <AlertTitle>Preview only</AlertTitle>
        This is a draft. Customers cannot see it until the menu is published.
        {data && data.expiresAt &&
          ` Link expires ${new Date(data.expiresAt).toLocaleDateString()}.`}
      </Alert>

      {warnings.length > 0 && (
        <Alert severity="warning" className={classes.banner}>
          <AlertTitle>
            {warnings.length} thing{warnings.length === 1 ? '' : 's'} to check before publishing
          </AlertTitle>
          {warnings.map((warning, index) => (
            <Typography variant="body2" key={`${warning.type}-${index}`}>
              {warning.category}
              {warning.item ? ` › ${warning.item}` : ''}
              {warning.group ? ` › ${warning.group}` : ''}: {warning.message}
            </Typography>
          ))}
        </Alert>
      )}

      {categories.length === 0 && (
        <Alert severity="warning">This menu has no categories yet.</Alert>
      )}

      {categories.map((category) => {
        const items = Array.isArray(category.items) ? category.items : [];
        return (
          <Paper key={category.id} className={classes.category} elevation={1}>
            <Typography variant="subtitle1" className={classes.categoryName}>
              {category.name}
              {category.altName ? ` ${category.altName}` : ''}
            </Typography>
            {category.description && (
              <Typography variant="body2" color="textSecondary">
                {category.description}
              </Typography>
            )}
            <Divider />
            {items.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.item}
              >
                No items in this category.
              </Typography>
            ) : (
              items.map((item) => (
                <PreviewItem
                  key={item.id}
                  item={item}
                  currency={currency}
                  classes={classes}
                />
              ))
            )}
          </Paper>
        );
      })}
    </Container>
  );
};

export default MenuPreview;
